"use client";

import { cn } from "@/lib/utils";
import { forwardRef, ReactNode, useEffect, useImperativeHandle, useRef, useState } from "react";

// ============ ScrollContainer 组件 ============
interface ScrollContainerProps {
    children: ReactNode;
    className?: string;
    showGradient?: boolean;
    gradientWidth?: string;
    gradientColor?: string;
    gap?: string;
    padding?: string;
}

export const ScrollContainer = forwardRef<HTMLDivElement, ScrollContainerProps>( ( {
    children,
    className,
    showGradient = true,
    gradientWidth = "w-5",
    gradientColor = "var(--ui-surface-base)",
    gap = "gap-[5px]",
    padding = "px-[0px]",
}, ref ) => {
    const scrollRef = useRef<HTMLDivElement>( null );

    useImperativeHandle( ref, () => scrollRef.current as HTMLDivElement );
    const [ showLeftGradient, setShowLeftGradient ] = useState( false );
    const [ showRightGradient, setShowRightGradient ] = useState( false );

    // 检查滚动状态
    const checkScroll = () => {
        if ( !scrollRef.current ) return;

        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;

        // 左侧遮罩：当滚动位置 > 0 时显示
        setShowLeftGradient( scrollLeft > 0 );

        // 右侧遮罩：当还有内容可以向右滚动时显示
        setShowRightGradient( scrollLeft < scrollWidth - clientWidth - 1 );
    };

    useEffect( () => {
        // 延迟检查，确保 DOM 完全渲染和内容加载完成
        const timer = setTimeout( () => {
            checkScroll();
        }, 100 );

        // 监听窗口大小变化
        window.addEventListener( "resize", checkScroll );

        return () => {
            clearTimeout( timer );
            window.removeEventListener( "resize", checkScroll );
        };
    }, [] );

    // 当 children 变化时重新检查
    useEffect( () => {
        const timer = setTimeout( () => {
            checkScroll();
        }, 100 );

        return () => clearTimeout( timer );
    }, [ children ] );

    return (
        <div className="relative">
            {/* 左侧渐变遮罩 - 只在需要时显示 */}
            {showGradient && showLeftGradient && (
                <div
                    className={cn(
                        "absolute left-0 top-0 bottom-0 z-10 pointer-events-none transition-opacity duration-300",
                        "bg-gradient-to-r",
                        gradientWidth,
                    )}
                    style={{
                        backgroundImage: `linear-gradient(to right, ${gradientColor}, transparent)`,
                    }}
                />
            )}

            {/* 滚动容器 */}
            <div
                ref={scrollRef}
                onScroll={checkScroll}
                className={cn(
                    "flex overflow-x-auto scrollbar-hide",
                    gap,
                    padding,
                    className,
                )}
            >
                {children}
            </div>

            {/* 右侧渐变遮罩 - 只在需要时显示 */}
            {showGradient && showRightGradient && (
                <div
                    className={cn(
                        "absolute right-0 top-0 bottom-0 z-10 pointer-events-none transition-opacity duration-300",
                        "bg-gradient-to-l",
                        gradientWidth,
                    )}
                    style={{
                        backgroundImage: `linear-gradient(to left, ${gradientColor}, transparent)`,
                    }}
                />
            )}
        </div>
    );
} );

ScrollContainer.displayName = "ScrollContainer";
